// AdminResetsPage — Queue of reset requests, cost calc, approve/reject
// Mirrors the customer ResetChallengePage but with bulk ops

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ResetRequest {
  id: string;
  trader: string;
  avatar: number;
  account: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  accountSize: number;
  currentPnl: number;
  daysUsed: number;
  totalDays: number;
  resetPrice: number;
  cost: number;            // 70% goes to system
  reason: string;
  requested: string;
  status: 'pending' | 'approved' | 'rejected' | 'processing';
}

const REQUESTS: ResetRequest[] = [
  { id: 'r1', trader: 'Marcus J.',  avatar: 1, account: 'FP-100K-14283', tier: 'Mid',          accountSize: 100000, currentPnl: -840,  daysUsed: 8,  totalDays: 30, resetPrice: 129, cost: 90.30, reason: 'Approached daily loss limit',          requested: '2h ago',  status: 'pending' },
  { id: 'r2', trader: 'Sarah C.',   avatar: 2, account: 'FP-150K-12844', tier: 'Professional', accountSize: 150000, currentPnl: 4280,  daysUsed: 23, totalDays: 30, resetPrice: 189, cost: 132.30, reason: 'Switching strategy',                  requested: '5h ago',  status: 'pending' },
  { id: 'r3', trader: 'Riley T.',   avatar: 3, account: 'FP-50K-15201',  tier: 'Beginner',     accountSize: 50000,  currentPnl: -240,  daysUsed: 14, totalDays: 30, resetPrice: 69,  cost: 48.30,  reason: 'Account breached on day 14',          requested: '1d ago',  status: 'pending' },
  { id: 'r4', trader: 'Devon K.',   avatar: 4, account: 'FP-100K-13892', tier: 'Mid',          accountSize: 100000, currentPnl: -1240, daysUsed: 30, totalDays: 30, resetPrice: 129, cost: 90.30, reason: 'No progress after 30 days',           requested: '2d ago',  status: 'approved' },
  { id: 'r5', trader: 'Lisa H.',    avatar: 1, account: 'FP-50K-15034',  tier: 'Beginner',     accountSize: 50000,  currentPnl: 1240,  daysUsed: 6,  totalDays: 30, resetPrice: 69,  cost: 48.30,  reason: 'Wants to start over for second attempt', requested: '3d ago', status: 'rejected' },
];

const TIER_COLOR = { Beginner: '#34C759', Mid: '#0A84FF', Professional: '#1C1C1E' };
const RESET_PRICE: Record<string, number> = { Beginner: 69, Mid: 129, Professional: 189 };

export function AdminResetsPage() {
  const nav = useNavigate();
  const [requests, setRequests] = useState(REQUESTS);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [selected, setSelected] = useState<string[]>([]);

  const filtered = requests.filter((r) => filter === 'all' || r.status === filter);

  function setStatus(id: string, status: ResetRequest['status']) {
    setRequests((curr) => curr.map((r) => (r.id === id ? { ...r, status } : r)));
  }

  function toggleSelect(id: string) {
    setSelected((curr) => curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]);
  }

  function bulkApprove() {
    selected.forEach((id) => setStatus(id, 'approved'));
    setSelected([]);
  }

  const pendingCount = requests.filter((r) => r.status === 'pending').length;
  const totalRevenue = requests.filter((r) => r.status === 'pending').reduce((s, r) => s + r.resetPrice, 0);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Resets</div>
        <div className="w-9" />
      </div>

      {/* Stats */}
      <div className="mx-4 mt-3 grid grid-cols-3 gap-2">
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Pending</div>
          <div className="text-[16px] font-bold text-[#FF9500]">{pendingCount}</div>
        </div>
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Revenue</div>
          <div className="text-[16px] font-bold text-[#34C759]">${totalRevenue}</div>
        </div>
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Cost</div>
          <div className="text-[16px] font-bold text-[#FF3B30]">${(totalRevenue * 0.7).toFixed(0)}</div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="mx-4 mt-3 rounded-[12px] border border-[#0A84FF] bg-[#0A84FF]/10 p-3 flex items-center gap-2">
          <div className="text-[12px] font-semibold text-white">{selected.length} selected</div>
          <div className="flex-1" />
          <button onClick={bulkApprove} data-testid="bulk-approve" className="h-8 px-3 rounded-full bg-[#34C759] text-black text-[12px] font-bold active:opacity-40">Approve all</button>
          <button onClick={() => setSelected([])} data-testid="bulk-cancel" className="h-8 px-3 rounded-full bg-white/10 text-white text-[12px] font-semibold active:opacity-40">Clear</button>
        </div>
      )}

      {/* Filters */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {(['pending', 'approved', 'rejected', 'all'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`rfilter-${f}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === f ? 'bg-white text-black' : 'bg-white/10 text-white/70 active:opacity-40'
            ].join(' ')}
          >{f}</button>
        ))}
      </div>

      {/* Request list */}
      <div className="px-4 mt-3 space-y-2">
        {filtered.map((r) => {
          const sColor = r.status === 'pending' ? '#FF9500' : r.status === 'approved' ? '#34C759' : '#FF3B30';
          return (
            <div key={r.id} data-testid={`reset-${r.id}`} className="rounded-[14px] border border-white/10 bg-white/5 p-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => r.status === 'pending' && toggleSelect(r.id)}
                  data-testid={`sel-${r.id}`}
                  className={['h-5 w-5 rounded-full border-2 shrink-0', selected.includes(r.id) ? 'bg-[#0A84FF] border-[#0A84FF]' : 'border-white/30', r.status !== 'pending' ? 'opacity-30' : ''].join(' ')}
                >
                  {selected.includes(r.id) && <svg viewBox="0 0 24 24" className="h-full w-full text-white" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>}
                </button>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[14px] font-bold" style={{ backgroundColor: TIER_COLOR[r.tier] + '30', color: TIER_COLOR[r.tier] }}>{r.trader[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-bold text-white">{r.trader}</span>
                    <span className="rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase" style={{ backgroundColor: sColor + '20', color: sColor }}>{r.status}</span>
                  </div>
                  <div className="text-[11px] text-white/50 font-mono">{r.account} · ${(r.accountSize/1000).toFixed(0)}K {r.tier}</div>
                  <p className="text-[10px] text-white/60 mt-0.5">"{r.reason}"</p>
                </div>
                <div className="text-right">
                  <div className="text-[16px] font-bold text-white tabular-nums">${r.resetPrice}</div>
                  <div className="text-[9px] text-white/40">cost ${r.cost.toFixed(2)}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] text-white/50">
                <span>Days used: {r.daysUsed}/{r.totalDays} · P&L ${r.currentPnl.toLocaleString()} · {r.requested}</span>
              </div>
              {r.status === 'pending' && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => setStatus(r.id, 'approved')} data-testid={`approve-${r.id}`} className="flex-1 h-8 rounded-full bg-[#34C759] text-black text-[12px] font-bold active:opacity-40">Approve</button>
                  <button onClick={() => setStatus(r.id, 'rejected')} data-testid={`reject-${r.id}`} className="flex-1 h-8 rounded-full bg-[#FF3B30] text-white text-[12px] font-bold active:opacity-40">Reject</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
